// src/components/tools/LexileLeveler.jsx
// -----------------------------------------------------------------------------
// King's Academy Faculty Tool Kit — Reading Level Adapter
//
// Teachers paste or upload an article, pick a target reading level by Lexile
// number or by grade, lock any terms that must survive, and get back a version
// of the text at that level plus an optional glossary.
//
// Deps:  npm install mammoth pdfjs-dist@3.11.174 jspdf
// Also:  cp node_modules/pdfjs-dist/legacy/build/pdf.worker.min.js public/
// Also:  copy wordFrequencies.json into public/data/
// -----------------------------------------------------------------------------

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { jsPDF } from 'jspdf';

import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  loadFrequencies,
  analyzeText,
  GRADE_BANDS,
  gradeToLexile,
  lexileToGradeLabel,
} from '../utils/readability';
import { levelText, verifyProtectedTerms } from '../utils/lexileEngine';
import { extractText } from '../utils/textExtract';

const KA = {
  red: '#E83530',
  navy: '#233C5F',
  gold: '#A29164',
  ink: '#1B2733',
  muted: '#6B7787',
  line: '#E2E6EB',
  panel: '#F7F8FA',
  white: '#FFFFFF',
  ok: '#2E7D5B',
  warn: '#B7791F',
};

const S = {
  page: { maxWidth: 1180, margin: '0 auto', padding: '24px 20px 80px', color: KA.ink,
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' },
  h1: { fontSize: 26, fontWeight: 700, color: KA.navy, margin: 0 },
  sub: { color: KA.muted, fontSize: 14, marginTop: 6, marginBottom: 24, lineHeight: 1.5 },
  card: { background: KA.white, border: `1px solid ${KA.line}`, borderRadius: 10,
    padding: 20, marginBottom: 18 },
  stepLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase',
    color: KA.gold, marginBottom: 10 },
  textarea: { width: '100%', minHeight: 200, padding: 12, border: `1px solid ${KA.line}`,
    borderRadius: 8, fontSize: 14, lineHeight: 1.6, fontFamily: 'inherit', resize: 'vertical',
    boxSizing: 'border-box' },
  input: { width: '100%', padding: '9px 11px', border: `1px solid ${KA.line}`, borderRadius: 7,
    fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' },
  select: { padding: '9px 11px', border: `1px solid ${KA.line}`, borderRadius: 7, fontSize: 14,
    fontFamily: 'inherit', background: KA.white, minWidth: 150 },
  btn: { background: KA.navy, color: KA.white, border: 'none', borderRadius: 7,
    padding: '11px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  btnPrimary: { background: KA.red, color: KA.white, border: 'none', borderRadius: 7,
    padding: '13px 26px', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  btnGhost: { background: KA.white, color: KA.navy, border: `1px solid ${KA.line}`,
    borderRadius: 7, padding: '9px 15px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'inherit' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: KA.navy, marginBottom: 6 },
  hint: { fontSize: 12, color: KA.muted, marginTop: 5, lineHeight: 1.5 },
  row: { display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' },
  error: { background: '#FDECEB', border: `1px solid ${KA.red}`, color: '#96201C',
    borderRadius: 7, padding: '11px 14px', fontSize: 13, marginBottom: 14, lineHeight: 1.5 },
  notice: { background: '#FDF6E7', border: `1px solid ${KA.gold}`, color: '#7A5C15',
    borderRadius: 7, padding: '11px 14px', fontSize: 13, marginBottom: 14, lineHeight: 1.5 },
};

// -----------------------------------------------------------------------------
// Sub-components — defined OUTSIDE the export so React does not remount them on
// every parent render.
// -----------------------------------------------------------------------------

function MeasureBadge({ analysis, caption }) {
  if (!analysis) return null;
  return (
    <div style={{ display: 'flex', gap: 22, alignItems: 'baseline', flexWrap: 'wrap' }}>
      <div>
        <div style={{ fontSize: 30, fontWeight: 700, color: KA.navy, lineHeight: 1 }}>
          {analysis.lexile}L
        </div>
        <div style={{ fontSize: 12, color: KA.muted, marginTop: 4 }}>
          {caption} · {analysis.gradeLabel}
        </div>
      </div>
      <div style={{ fontSize: 12, color: KA.muted, lineHeight: 1.7 }}>
        {analysis.stats.words.toLocaleString()} words · {analysis.stats.sentences} sentences ·{' '}
        {analysis.stats.meanSentenceLength} words per sentence · Flesch–Kincaid {analysis.fkgl}
      </div>
    </div>
  );
}

function TargetPicker({ grade, lexile, onGradeChange, onLexileChange }) {
  return (
    <div style={S.row}>
      <div>
        <label style={S.label} htmlFor="ka-grade">Reading level by grade</label>
        <select
          id="ka-grade"
          style={S.select}
          value={grade}
          onChange={(e) => onGradeChange(e.target.value)}
        >
          <option value="">Choose a grade…</option>
          {GRADE_BANDS.map((b) => (
            <option key={b.grade} value={b.grade}>
              {b.label} ({b.min}–{b.max}L)
            </option>
          ))}
        </select>
      </div>

      <div style={{ flex: 1, minWidth: 280 }}>
        <label style={S.label} htmlFor="ka-lexile">
          Or set the number directly — {lexile}L ({lexileToGradeLabel(lexile)})
        </label>
        <input
          id="ka-lexile"
          type="range"
          min={200}
          max={1400}
          step={10}
          value={lexile}
          onChange={(e) => onLexileChange(Number(e.target.value))}
          style={{ width: '100%', accentColor: KA.red }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', ...S.hint }}>
          <span>200L</span><span>1400L</span>
        </div>
      </div>
    </div>
  );
}

function GlossaryTable({ glossary }) {
  if (!glossary || glossary.length === 0) return null;
  return (
    <div style={{ marginTop: 24 }}>
      <div style={S.stepLabel}>Glossary — {glossary.length} entries</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <tbody>
          {glossary.map((entry) => (
            <tr key={entry.term} style={{ borderBottom: `1px solid ${KA.line}` }}>
              <td style={{ padding: '9px 12px 9px 0', fontWeight: 600, color: KA.navy,
                width: '26%', verticalAlign: 'top' }}>
                {entry.term}
              </td>
              <td style={{ padding: '9px 0', lineHeight: 1.6 }}>{entry.definition}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TermCheck({ results }) {
  if (!results || results.length === 0) return null;
  const missing = results.filter((r) => !r.present);
  if (missing.length === 0) {
    return (
      <div style={{ fontSize: 13, color: KA.ok, marginTop: 12 }}>
        All {results.length} required terms are present in the adapted text.
      </div>
    );
  }
  return (
    <div style={{ ...S.notice, marginTop: 12, marginBottom: 0 }}>
      These required terms did not make it into the adapted text:{' '}
      <strong>{missing.map((m) => m.term).join(', ')}</strong>. Add them back by hand, or run it
      again — the model occasionally paraphrases a term it should have kept.
    </div>
  );
}

function TextPanel({ title, body, meta }) {
  return (
    <div style={{ flex: 1, minWidth: 300 }}>
      <div style={{ ...S.stepLabel, color: KA.navy }}>{title}</div>
      {meta}
      <div style={{ marginTop: 12, padding: 16, background: KA.panel,
        border: `1px solid ${KA.line}`, borderRadius: 8, maxHeight: 520, overflowY: 'auto',
        fontSize: 15, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
        {body}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

export default function LexileLeveler() {
  const { user, isSuperAdmin } = useAuth();

  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [sourceText, setSourceText] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [sourceAnalysis, setSourceAnalysis] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [scanWarning, setScanWarning] = useState(false);

  const [grade, setGrade] = useState('');
  const [targetLexile, setTargetLexile] = useState(800);
  const [protectedInput, setProtectedInput] = useState('');
  const [includeGlossary, setIncludeGlossary] = useState(true);

  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [sideBySide, setSideBySide] = useState(true);

  const fileInputRef = useRef(null);

  const protectedTerms = useMemo(
    () => protectedInput.split(/[\n,]/).map((t) => t.trim()).filter(Boolean),
    [protectedInput]
  );

  useEffect(() => {
    let cancelled = false;
    loadFrequencies()
      .then(() => { if (!cancelled) setReady(true); })
      .catch((err) => { if (!cancelled) setLoadError(err.message); });
    return () => { cancelled = true; };
  }, []);

  // Re-measure the source as the teacher types or edits, debounced.
  useEffect(() => {
    if (!ready) return undefined;
    const timer = setTimeout(() => {
      try {
        setSourceAnalysis(analyzeText(sourceText, { protectedTerms }));
      } catch {
        setSourceAnalysis(null);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [sourceText, ready, protectedTerms]);

  const handleGradeChange = useCallback((value) => {
    setGrade(value);
    const lex = gradeToLexile(value);
    if (lex) setTargetLexile(lex);
  }, []);

  const handleLexileChange = useCallback((value) => {
    setTargetLexile(value);
    setGrade('');
  }, []);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setError('');
    setScanWarning(false);
    setExtracting(true);
    try {
      const { text, likelyScanned } = await extractText(file);
      if (!text) {
        setError(`No text came out of ${file.name}. If it is a scan, the page images hold no text layer.`);
      } else {
        setSourceText(text);
        setSourceName(file.name);
        setScanWarning(likelyScanned);
        setResult(null);
        setSaved(false);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setExtracting(false);
    }
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    handleFile(event.dataTransfer.files?.[0]);
  }, [handleFile]);

  const handleLevel = useCallback(async () => {
    setError('');
    setResult(null);
    setSaved(false);
    setWorking(true);
    setProgress('Preparing the text…');

    try {
      const output = await levelText({
        sourceText,
        targetLexile,
        protectedTerms,
        includeGlossary,
        onProgress: ({ stage, chunk, chunkCount, attempt, measured }) => {
          if (stage === 'generating') {
            const section = chunkCount > 1 ? ` — section ${chunk} of ${chunkCount}` : '';
            setProgress(attempt === 1
              ? `Rewriting${section}…`
              : `Adjusting the level, pass ${attempt}${section}…`);
          } else if (stage === 'measured' && measured) {
            setProgress(`Measured at ${measured.lexile}L. Target is ${targetLexile}L.`);
          } else if (stage === 'done') {
            setProgress('Finishing up…');
          }
        },
      });
      setResult(output);
      setProgress('');
    } catch (err) {
      setError(err.message);
      setProgress('');
    } finally {
      setWorking(false);
    }
  }, [sourceText, targetLexile, protectedTerms, includeGlossary]);

  const handleSave = useCallback(async () => {
    if (!result) return;
    try {
      await addDoc(collection(db, 'lexileTransforms'), {
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
        createdByEmail: user?.email || null,
        schoolYear: '2025-2026',
        sourceName: sourceName || 'Pasted text',
        sourceLexile: result.sourceAnalysis.lexile,
        sourceWordCount: result.sourceAnalysis.stats.words,
        targetLexile,
        resultLexile: result.analysis ? result.analysis.lexile : null,
        protectedTerms,
        includeGlossary,
        glossary: result.glossary,
        sourceText,
        leveledText: result.text,
        model: 'claude-sonnet-4-6',
      });
      setSaved(true);
    } catch (err) {
      setError(`Could not save: ${err.message}`);
    }
  }, [result, user, sourceName, sourceText, targetLexile, protectedTerms, includeGlossary]);

  const handleCopy = useCallback(() => {
    if (!result) return;
    const glossaryBlock = result.glossary.length
      ? `\n\nGLOSSARY\n${result.glossary.map((g) => `${g.term} — ${g.definition}`).join('\n')}`
      : '';
    navigator.clipboard.writeText(result.text + glossaryBlock);
  }, [result]);

  const handleExportPdf = useCallback(() => {
    if (!result) return;
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const margin = 56;
    const width = doc.internal.pageSize.getWidth() - margin * 2;
    const bottom = doc.internal.pageSize.getHeight() - margin;
    let y = margin;

    const nextPageIfNeeded = (needed) => {
      if (y + needed > bottom) { doc.addPage(); y = margin; }
    };

    doc.setFont('helvetica', 'bold').setFontSize(15).setTextColor(35, 60, 95);
    doc.text(sourceName || 'Adapted reading', margin, y);
    y += 18;

    doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(120, 130, 145);
    doc.text(
      `Adapted to approximately ${result.analysis ? result.analysis.lexile : targetLexile}L · ${lexileToGradeLabel(targetLexile)} · King's Academy`,
      margin, y
    );
    y += 24;

    doc.setFontSize(11).setTextColor(27, 39, 51);
    result.text.split(/\n\s*\n/).forEach((para) => {
      const lines = doc.splitTextToSize(para.trim(), width);
      lines.forEach((line) => {
        nextPageIfNeeded(16);
        doc.text(line, margin, y);
        y += 16;
      });
      y += 8;
    });

    if (result.glossary.length) {
      nextPageIfNeeded(50);
      y += 12;
      doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(35, 60, 95);
      doc.text('Glossary', margin, y);
      y += 18;
      doc.setFontSize(10).setTextColor(27, 39, 51);
      result.glossary.forEach((entry) => {
        const lines = doc.splitTextToSize(`${entry.term} — ${entry.definition}`, width);
        nextPageIfNeeded(lines.length * 14 + 6);
        doc.setFont('helvetica', 'bold').text(entry.term, margin, y);
        const offset = doc.getTextWidth(entry.term) + 6;
        doc.setFont('helvetica', 'normal');
        const defLines = doc.splitTextToSize(`— ${entry.definition}`, width - offset);
        doc.text(defLines[0] || '', margin + offset, y);
        y += 14;
        defLines.slice(1).forEach((line) => {
          nextPageIfNeeded(14);
          doc.text(line, margin, y);
          y += 14;
        });
        y += 4;
      });
    }

    const safe = (sourceName || 'adapted-reading').replace(/\.[^.]+$/, '').replace(/[^\w-]+/g, '-');
    doc.save(`${safe}-${targetLexile}L.pdf`);
  }, [result, sourceName, targetLexile]);

  const termResults = result ? verifyProtectedTerms(result.text, protectedTerms) : null;
  const canLevel = ready && !working && sourceAnalysis && sourceAnalysis.stats.words >= 25;

  if (loadError) {
    return (
      <div style={S.page}>
        <h1 style={S.h1}>Reading Level Adapter</h1>
        <div style={{ ...S.error, marginTop: 20 }}>
          The word frequency list did not load, so nothing can be measured. {loadError}{' '}
          Check that <code>wordFrequencies.json</code> is deployed at{' '}
          <code>/data/wordFrequencies.json</code>.
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Reading Level Adapter</h1>
      <p style={S.sub}>
        Rewrite an article at a reading level a student can actually work with, without losing what
        the article says. Paste text or upload a PDF or Word file, choose a target, and lock any
        vocabulary that has to survive the rewrite.
      </p>

      {error && <div style={S.error}>{error}</div>}

      {/* ---------------------------------------------------------------- */}
      <div style={S.card}>
        <div style={S.stepLabel}>1 · The text</div>

        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          style={{ border: `2px dashed ${KA.line}`, borderRadius: 8, padding: '18px 20px',
            marginBottom: 14, textAlign: 'center', background: KA.panel }}
        >
          <div style={{ fontSize: 14, color: KA.muted }}>
            Drop a PDF, .docx, or .txt file here, or{' '}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{ background: 'none', border: 'none', color: KA.red, fontWeight: 600,
                cursor: 'pointer', fontSize: 14, padding: 0, fontFamily: 'inherit',
                textDecoration: 'underline' }}
            >
              choose a file
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.txt,.md"
            style={{ display: 'none' }}
            onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
          />
          {extracting && (
            <div style={{ ...S.hint, marginTop: 8 }}>Reading the file…</div>
          )}
          {sourceName && !extracting && (
            <div style={{ ...S.hint, marginTop: 8 }}>Loaded from <strong>{sourceName}</strong></div>
          )}
        </div>

        {scanWarning && (
          <div style={S.notice}>
            This PDF has very little text on the page, which usually means it is a scan. Scanned
            pages hold images, not words, so there is nothing to measure or rewrite. Try a text-based
            PDF, or copy the text in by hand.
          </div>
        )}

        <textarea
          style={S.textarea}
          value={sourceText}
          onChange={(e) => { setSourceText(e.target.value); setResult(null); setSaved(false); }}
          placeholder="Or paste the article here. Keep the blank line between paragraphs — the adapted version is matched paragraph for paragraph so you can put the two side by side in class."
        />

        {sourceAnalysis && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${KA.line}` }}>
            <MeasureBadge analysis={sourceAnalysis} caption="Estimated source level" />
          </div>
        )}
        {sourceText && !sourceAnalysis && ready && (
          <div style={{ ...S.hint, marginTop: 12 }}>
            Add at least 25 words before this can be measured.
          </div>
        )}
      </div>

      {/* ---------------------------------------------------------------- */}
      <div style={S.card}>
        <div style={S.stepLabel}>2 · The target</div>
        <TargetPicker
          grade={grade}
          lexile={targetLexile}
          onGradeChange={handleGradeChange}
          onLexileChange={handleLexileChange}
        />

        <div style={{ marginTop: 22 }}>
          <label style={S.label} htmlFor="ka-protected">Terms and concepts to keep</label>
          <input
            id="ka-protected"
            style={S.input}
            value={protectedInput}
            onChange={(e) => setProtectedInput(e.target.value)}
            placeholder="photosynthesis, chloroplast, the Columbian Exchange"
          />
          <div style={S.hint}>
            Separate with commas. These stay word for word in the adapted text — the sentences around
            them get simpler instead, and each one goes into the glossary. Use this for vocabulary a
            student is meant to walk away owning.
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeGlossary}
              onChange={(e) => setIncludeGlossary(e.target.checked)}
              style={{ marginTop: 3, accentColor: KA.red }}
            />
            <span>
              <span style={{ fontSize: 14, fontWeight: 600, color: KA.navy }}>Build a glossary</span>
              <span style={S.hint}>
                Defines the terms you locked above, plus idioms, figures of speech, and cultural
                references that a language learner would read straight past. Definitions are written
                at the target level, not above it.
              </span>
            </span>
          </label>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 28 }}>
        <button
          type="button"
          onClick={handleLevel}
          disabled={!canLevel}
          style={{ ...S.btnPrimary, opacity: canLevel ? 1 : 0.45,
            cursor: canLevel ? 'pointer' : 'not-allowed' }}
        >
          {working ? 'Working…' : `Rewrite at ${targetLexile}L`}
        </button>
        {progress && <span style={{ fontSize: 13, color: KA.muted }}>{progress}</span>}
        {!ready && !loadError && (
          <span style={{ fontSize: 13, color: KA.muted }}>Loading the frequency list…</span>
        )}
      </div>

      {/* ---------------------------------------------------------------- */}
      {result && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
            <MeasureBadge analysis={result.analysis} caption="Adapted level" />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" style={S.btnGhost} onClick={() => setSideBySide((v) => !v)}>
                {sideBySide ? 'Stack' : 'Side by side'}
              </button>
              <button type="button" style={S.btnGhost} onClick={handleCopy}>Copy</button>
              <button type="button" style={S.btnGhost} onClick={handleExportPdf}>Export PDF</button>
              <button type="button" style={S.btn} onClick={handleSave} disabled={saved}>
                {saved ? 'Saved' : 'Save to library'}
              </button>
            </div>
          </div>

          {result.analysis && Math.abs(result.analysis.lexile - targetLexile) > 100 && (
            <div style={S.notice}>
              This landed at {result.analysis.lexile}L against a target of {targetLexile}L. That gap
              usually means the required terms are doing the work — a text built around locked
              technical vocabulary can only get so simple. Read it before you use it.
            </div>
          )}

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {sideBySide && (
              <TextPanel
                title="Original"
                body={sourceText}
                meta={<div style={S.hint}>{result.sourceAnalysis.lexile}L · {result.sourceAnalysis.stats.words} words</div>}
              />
            )}
            <TextPanel
              title="Adapted"
              body={result.text}
              meta={<div style={S.hint}>
                {result.analysis ? `${result.analysis.lexile}L · ${result.analysis.stats.words} words` : ''}
              </div>}
            />
          </div>

          <TermCheck results={termResults} />
          <GlossaryTable glossary={result.glossary} />

          {isSuperAdmin && result.log.length > 1 && (
            <details style={{ marginTop: 22, fontSize: 12, color: KA.muted }}>
              <summary style={{ cursor: 'pointer' }}>Revision passes ({result.log.length})</summary>
              <ul style={{ marginTop: 8, lineHeight: 1.7 }}>
                {result.log.map((entry, i) => (
                  <li key={i}>
                    Section {entry.chunk}, pass {entry.attempt}: {entry.lexile}L,{' '}
                    {entry.meanSentenceLength} words per sentence
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <p style={{ ...S.hint, marginTop: 32, paddingTop: 16, borderTop: `1px solid ${KA.line}` }}>
        Levels shown here are estimates on the Lexile scale, calculated from sentence length and word
        frequency. They are not official Lexile® measures, which are produced by MetaMetrics, Inc.
        Expect them to sit within roughly 60–100L of an official measure. Always read an adapted text
        before handing it to students.
      </p>
    </div>
  );
}
